import { useEffect, useMemo, useRef, useState } from "react";
import appConfig from "../../../constants/config";

const resolveHealthUrl = () => {
  const serverUrl = appConfig?.SERVER_URL;
  if (!serverUrl) return "/health";

  return `${serverUrl.replace(/\/$/, "")}/health`;
};

const DEFAULT_OPTIONS = {
  url: resolveHealthUrl(),
  interval: 5000,
  timeout: 3000,
  sampleSize: 5,
  active: true,
  credentials: "include",
  qualityMode: "instant",
};

const classifyQuality = ({ latency, errorRate, consecutiveFailures }) => {
  if (consecutiveFailures >= 2 || errorRate > 0.4 || latency === Infinity) {
    return "poor";
  }
  if (latency == null) {
    return "unknown";
  }
  if (latency >= 450 || errorRate > 0.25) {
    return "poor";
  }
  if (latency >= 200 || errorRate > 0.1) {
    return "good";
  }
  return "excellent";
};

export const useHttpPing = (options) => {
  const settings = useMemo(
    () => ({ ...DEFAULT_OPTIONS, ...(options || {}) }),
    [options]
  );

  const attemptsRef = useRef(0);
  const failuresRef = useRef(0);
  const consecutiveFailuresRef = useRef(0);
  const samplesRef = useRef([]);
  const [metrics, setMetrics] = useState({
    latency: null,
    lastLatency: null,
    quality: "unknown",
    errorRate: 0,
    lastUpdated: null,
    lastError: null,
    consecutiveFailures: 0,
  });

  useEffect(() => {
    if (!settings.active) {
      return undefined;
    }

    let intervalId = null;
    let cancelled = false;

    const recordResult = (latency, success, error) => {
      attemptsRef.current += 1;

      if (success) {
        consecutiveFailuresRef.current = 0;
        if (latency != null) {
          samplesRef.current = [
            ...samplesRef.current.slice(
              Math.max(0, samplesRef.current.length - (settings.sampleSize - 1))
            ),
            latency,
          ];
        }
      } else {
        failuresRef.current += 1;
        consecutiveFailuresRef.current += 1;
      }

      const errorRate =
        attemptsRef.current === 0
          ? 0
          : failuresRef.current / attemptsRef.current;

      const latencyAverage =
        samplesRef.current.length === 0
          ? null
          : samplesRef.current.reduce((acc, value) => acc + value, 0) /
            samplesRef.current.length;

      const latencyForQuality =
        settings.qualityMode === "instant"
          ? success
            ? latency
            : Infinity
          : latencyAverage;

      const quality = classifyQuality({
        latency:
          latencyForQuality ??
          (success ? latencyAverage : Infinity) ??
          (success ? latency : null),
        errorRate,
        consecutiveFailures: consecutiveFailuresRef.current,
      });

      const nextMetrics = {
        latency: latencyAverage,
        lastLatency: success ? latency : null,
        quality,
        errorRate,
        lastUpdated: Date.now(),
        lastError: success ? null : error?.message ?? "Request failed",
        consecutiveFailures: consecutiveFailuresRef.current,
      };

     

      setMetrics(nextMetrics);
    };

    const ping = async () => {
      if (cancelled || document?.hidden) {
        return;
      }

      const abortController = new AbortController();
      const timeoutId = setTimeout(() => {
        abortController.abort();
      }, settings.timeout);

      const start = performance.now();

      try {
        const response = await fetch(settings.url, {
          method: "GET",
          credentials: settings.credentials,
          cache: "no-store",
          signal: abortController.signal,
        });

        const latency = performance.now() - start;
        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        recordResult(latency, true);
      } catch (error) {
        clearTimeout(timeoutId);
        recordResult(null, false, error);
      }
    };

    ping();
    intervalId = setInterval(ping, settings.interval);

    return () => {
      cancelled = true;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [settings]);

  return metrics;
};

export const useConnectionMonitor = useHttpPing;

